import { useMutation } from '@apollo/client';
import { useRouteParams } from '@/contexts/RouteContext';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useOne } from '@/hooks/resourceQueries';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import type {
	TariffQuery,
	UpdateTariffMutation,
	UpdateTariffMutationVariables
} from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { TariffForm } from './tariff-form';
import { buildUpdateTariffInput, type TariffFormValues } from './tariff-input';
import { TARIFF_QUERY, TARIFF_UPDATE_MUTATION } from './tariffs.gql.tsx';

type TariffRecord = NonNullable<TariffQuery['tariff']>;

const isValidUlid = (id: string): boolean => {
	if (!id?.length || id.length !== 26) return false;
	const ulidRegex = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;
	return ulidRegex.test(id.toUpperCase());
};

export const TariffsEdit = () => {
	const go = useGo();
	const { id } = useRouteParams();
	useEditPageHeader({ id, resourceName: 'tariffs' });

	const {
		data: tariffResponse,
		isLoading,
		isError,
		error: fetchError
	} = useOne<TariffRecord>({
		resource: 'tariffs',
		id: id ?? '',
		meta: {
			gqlQuery: TARIFF_QUERY,
			dataPath: 'tariff'
		},
		queryOptions: {
			enabled: Boolean(id)
		}
	});

	const [updateTariff, { loading: isSubmitting }] = useMutation<
		UpdateTariffMutation,
		UpdateTariffMutationVariables
	>(TARIFF_UPDATE_MUTATION);

	const tariff = tariffResponse?.data;

	if (id && !isValidUlid(id)) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="max-w-md sentry-card">
					<div className="space-y-4 text-center sentry-card-body">
						<div className="text-lg font-medium text-red-500">
							Invalid Tariff ID
						</div>
						<p className="text-gray-600">
							The tariff ID format is invalid:{' '}
							<code className="px-2 py-1 bg-gray-100 rounded">
								{id}
							</code>
						</p>
						<p className="text-sm text-gray-500">
							Expected a valid ULID (26 character alphanumeric
							string)
						</p>
						<button
							onClick={() => go({ to: '/tariffs' })}
							className="sentry-btn-primary"
						>
							Back to Tariffs
						</button>
					</div>
				</div>
			</div>
		);
	}

	const handleSubmit = async (formData: TariffFormValues) => {
		if (!tariff?.tariffId) {
			showMutationErrorToast('tariffs', 'update', {
				message: 'Tariff identifier is missing.'
			});
			return;
		}

		const payload = buildUpdateTariffInput(formData, tariff.tariffId);

		try {
			const { data } = await updateTariff(
				getMutationOptions('tariffs', { input: payload })
			);

			const message = extractCheckResultError(data?.updateTariff);

			if (message) {
				showMutationErrorToast('tariffs', 'update', { message });
				return;
			}

			showMutationSuccessToast('tariffs', 'update');
			go({
				to: '/tariffs',
				state: { selectTariffId: tariff.tariffId }
			});
		} catch (error) {
			showMutationErrorToast('tariffs', 'update', { error });
		}
	};

	if (!id) {
		return (
			<div className="p-6">
				<Alert variant="destructive">
					<AlertTitle>Tariff not found</AlertTitle>
					<AlertDescription>
						A tariff identifier was not provided in the URL.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="flex items-center gap-4">
					<Button
						variant="outline"
						onClick={() => go({ to: '/tariffs' })}
					>
						← Back
					</Button>
					<h1 className="text-2xl font-bold">Edit Tariff</h1>
				</div>
				<Card className="text-sm text-muted-foreground">
					Loading tariff details...
				</Card>
			</div>
		);
	}

	if (isError || !tariff) {
		return (
			<div className="p-4">
				<Alert variant="destructive">
					<AlertTitle>Unable to load tariff</AlertTitle>
					<AlertDescription>
						{fetchError?.message ??
							'The requested tariff could not be found.'}
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<FormPageLayout>
			<TariffForm
				key={id}
				mode="edit"
				initialData={tariff}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Edit Tariff"
				onBack={() => go({ to: '/tariffs' })}
			/>
		</FormPageLayout>
	);
};
